import { PropertyValueMap, html, nothing } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { property, query, queryAssignedElements } from 'lit/decorators.js';
import 'element-internals-polyfill';
import { pdsCustomElement as customElement } from '../../decorators/pds-custom-element';
import { PdsElement } from '../PdsElement';
import styles from './button.scss?inline';
import '@principal/design-system-icons-web/loader';
import { getAriaDescribedByElementIds } from '../../../utils/get-aria-described-by-element-ids';

export type ButtonVariant =
  | 'default'
  | 'default-inverted'
  | 'primary'
  | 'primary-inverted'
  | 'icon'
  | 'icon-inverted'
  | 'loading';

const size = ['default', 'sm'] as const;
export type ButtonSize = (typeof size)[number];

/**
 * @summary A styled button element
 *
 * @slot default Optional: The contents of the button, should only contain text
 * @slot icon-right Optional: Holds an icon to the right of the link, restricted to pds-icon
 * @slot icon-left Optional: Holds an icon to the left of the link, restricted to pds-icon
 *
 * @fires pds-button-click A custom event dispatched on click
 */
@customElement('pds-button', {
  category: 'component',
  type: 'component',
  state: 'stable',
  styles,
})
export class PdsButton extends PdsElement {
  /**
   * @internal
   */
  static formAssociated = true;

  /**
   * @internal
   */
  internals: Omit<
    ElementInternals,
    'ariaBrailleLabel' | 'ariaBrailleRoleDescription'
  >;

  constructor() {
    super();
    this.internals = this.attachInternals();
  }

  /**
   * - **default** renders the button used for the most common calls to action that don't require as much visual attention.
   * - **default-inverted** renders a default button for use on darker backgrounds.
   * - **primary** renders the button used for the most important calls to action.
   * - **primary-inverted** renders a primary button for use on darker backgrounds.
   * - **icon** renders the button used for icon.
   * - **icon-inverted** renders the button for icons used on darker backgrounds.
   * - **loading** renders a disabled button with a spinning loading icon.
   */
  @property()
  variant: ButtonVariant = 'default';

  /**
   * Small button
   */
  @property()
  size: ButtonSize = 'default';

  /**
   * Disabled button
   */
  @property({ type: Boolean })
  disabled: boolean = false;

  /**
   * Disabled button description
   */
  @property()
  disabledContext: string;

  /**
   * FullWidth button
   */
  @property({ type: Boolean })
  fullWidth: boolean = false;

  /**
   * - **submit** specifies submit type button for form-data submit, default
   * - **reset** specifies reset type button for form-data reset
   * - **button** specifies clickable button type
   */
  @property({
    reflect: true,
  })
  type: 'submit' | 'reset' | 'button' = 'submit';

  // TODOv4: remove deprecated emphasis variants that are not a11y-compliant
  /**
   * Render the button as a link variant
   *
   * - **default** renders link for basic action
   * - **DEPRECATED**
   *     - **emphasis** provide more affordance
   *     - **emphasis-inverted** provide more affordance on a darker background
   * - **icon-left** link with icon left
   * - **icon-right** link with icon right
   */
  @property()
  link:
    | 'default'
    | 'inverted'
    | 'emphasis'
    | 'emphasis-inverted'
    | 'icon-left'
    | 'icon-right'
    | '' = '';

  /**
   * Remove padding from link button. Default is false.
   */
  @property({ type: Boolean })
  removeLinkPadding: boolean = false;

  /**
   * Screen reader label for button
   */
  @property({ type: String, reflect: true })
  ariaLabel: string;

  /**
   * A space-separated list of element IDs that the button controls
   */
  @property({ type: String })
  ariaControls: string;

  // TODOv4: Remove this property in favor of ariaDescribedbyIds
  /**
   * A space-separated list of element IDs that provide additional information to the user about the link
   * There needs to be an element with the same ID as the value of this property in the light DOM or else the aria-describedby attribute will not be set.
   * **DEPRECATED**
   */
  @property()
  ariaDescribedby: string;

  /**
   *
   * A space-separated list of element IDs that provide additional information to the user about the link
   * There needs to be an associated element with the same ID in the light DOM or else the aria-describedby attribute will not be set.
   */
  @property()
  ariaDescribedbyIds: string;

  /**
   * This is a faux focus effect to show the effect of being focused without
   * actually being focused.
   */
  @property({ type: Boolean })
  isActive: boolean = false;

  /**
   * @internal
   */
  @query('button')
  button: HTMLButtonElement;

  /**
   * This grabs the iconLeft slot
   * @internal
   */
  @queryAssignedElements({ slot: 'icon-left' })
  iconLeftList: HTMLElement[];

  /**
   * This grabs the iconRight slot
   * @internal
   */
  @queryAssignedElements({ slot: 'icon-right' })
  iconRightList: HTMLElement[];

  // We could re-render here to make sure slot changes from
  // icon-left to icon-right are handled on the fly, but that's
  // likely not going to be a need for users.  If it ever does
  // become an issue, then we can address it here by re-rendering
  // with something like requestUpdate().
  handleSlotChange(e: Event) {
    this.handleSlotValidation(e);
    // TODOv4: If we are removing NSKv1 support, move verifyAria out of the setTimeout and delete the setTimeout function.
    // This isn't needed once we drop NSK v1 support.  NSK v2 handles this properly.
    setTimeout(async () => {
      this.verifyAria();
    }, 100);
    this.addSizeToButtonIcon();
  }

  /**
   * This grabs the content from the default slot
   * @internal
   */
  @queryAssignedElements({ slot: undefined })
  defaultSlotElements: HTMLElement[];

  addSizeToButtonIcon() {
    const icons = [
      ...this.iconLeftList,
      ...this.iconRightList,
      ...this.defaultSlotElements,
    ];
    if (icons && icons.length) {
      icons.forEach((icon) => {
        if (icon.tagName.toLowerCase().includes('pds-icon')) {
          if (this.variant === 'icon' || this.variant === 'icon-inverted') {
            if (size.includes(this.size)) {
              icon.setAttribute('size', this.size === 'sm' ? 'default' : 'lg');
            } else {
              icon.setAttribute('size', 'lg');
            }
          } else if (size.includes(this.size)) {
            icon.setAttribute('size', this.size);
          } else {
            icon.setAttribute('size', 'default');
          }
        }
      });
    }
  }

  private handleClick(e: MouseEvent) {
    let eventMessage;

    if (this.textContent?.trim() === '') {
      eventMessage = this.ariaLabel;
    } else {
      eventMessage = this.textContent;
    }

    if (this.variant === 'loading' || this.disabled) {
      e.preventDefault();
      return;
    }

    const customEvent = new CustomEvent('pds-button-click', {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: {
        summary: eventMessage,
      },
    });

    this.dispatchEvent(customEvent);

    if (customEvent.defaultPrevented) {
      e.preventDefault();
    } else {
      this.submitOrResetAssociatedForm();
    }
  }

  private handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      this.submitOrResetAssociatedForm();
    }
  }

  private submitOrResetAssociatedForm() {
    // Needed for submit and reset buttons
    if (this.type === 'submit') {
      // Older versions of Safari don't support the requestSubmit method
      if (this.internals.form?.requestSubmit) {
        this.internals.form.requestSubmit();
      } else {
        this.internals.form?.submit();
      }
    }

    if (this.type === 'reset') {
      this.internals.form?.reset();
    }
  }

  /**
   * @internal
   *
   * @returns boolean indicating whether or not button has valid screen readable text associated
   */
  verifyAria() {
    const hasLabel = !!this.ariaLabel || !!this.textContent?.trim();

    if (!hasLabel) {
      console.error(
        'Button text is required as an ariaLabel property or as a slot in <%s /> but is undefined on: %o',
        this.tagName.toLowerCase(),
        this,
      );
    }

    return hasLabel;
  }

  /**
   * @internal
   */
  get classNames() {
    return {
      [this.variant]: !!this.variant,
      sm: this.size === 'sm',
      'is-active': this.isActive,
      link: !!this.link,
      [`link-${this.link}`]: !!this.link,
      'remove-link-padding': this.removeLinkPadding,
      fullWidth: this.fullWidth === true,
    };
  }

  protected override async firstUpdated() {
    super.firstUpdated();
    this.handleSlotValidation('icon-left');
    this.handleSlotValidation('icon-right');
    this.handleSlotValidation();
    await this.updateComplete;
    // TODOv4: If we are removing NSKv1 support, move verifyAria out of the setTimeout and delete the setTimeout function.
    // This isn't needed once we drop NSK v1 support.  NSK v2 handles this properly.
    setTimeout(async () => {
      this.verifyAria();
    }, 1000);
    this.addSizeToButtonIcon();
  }

  async updated(
    changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ) {
    await this.updateComplete;
    this.addSizeToButtonIcon();

    if (changedProperties.has('disabled') && this.disabled) {
      this.isActive = false;
    }

    // TODOv4: Remove the logic for ariaDescribedby once we remove the property
    if (
      changedProperties.has('ariaDescribedbyIds') ||
      changedProperties.has('ariaDescribedby') ||
      changedProperties.has('disabledContext')
    ) {
      const ariaDescribedbyIds = getAriaDescribedByElementIds(this);
      this.button.setAttribute('aria-describedby', ariaDescribedbyIds);
    }
  }

  render() {
    // TODOv4: remove this deprecation warning
    if (this.link === 'emphasis' || this.link === 'emphasis-inverted') {
      const newVariant =
        this.link === 'emphasis-inverted' ? 'inverted' : 'default';
      console.warn(
        `The ${this.link} link button variant is deprecated and will be removed in the next major version of PDS.  Please use the ${newVariant} variant instead.`,
      );
    }

    return html`<button
        class=${this.getClass()}
        ?aria-disabled=${this.disabled}
        part="button"
        aria-label=${ifDefined(this.ariaLabel)}
        title=${this.variant === 'icon' || this.variant === 'icon-inverted'
          ? ifDefined(this.ariaLabel)
          : nothing}
        aria-controls=${ifDefined(this.ariaControls)}
        aria-expanded=${ifDefined(this.ariaExpanded)}
        type=${this.type}
        @click=${this.handleClick}
        @keydown=${this.handleKeydown}
      >
        ${this.variant === 'loading'
          ? html`<pds-icon-loader size="${this.size}"></pds-icon-loader>`
          : ''}
        ${this.slotNotEmpty('icon-left')
          ? html`<slot
              allowed-elements="pds-icon"
              name="icon-left"
              @slotchange=${this.handleSlotChange}
            ></slot>`
          : ''}
        <slot @slotchange=${this.handleSlotChange}></slot>${this.disabled
          ? html` <span class="pds-u-sr-only"
              >, ${this.translateText('disabled')}</span
            >`
          : ''}
        ${this.slotNotEmpty('icon-right')
          ? html`<slot
              allowed-elements="pds-icon"
              name="icon-right"
              @slotchange=${this.handleSlotChange}
            ></slot>`
          : ''}</button
      >${this.disabledContext &&
      html`<div
        id="disabled-context"
        class="${this.classEl('disabled-context')}"
      >
        ${this.disabledContext}
      </div>`}`;
  }
}
